import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { MapPin, ArrowRight } from "lucide-react";
import { CITY_NAME_TO_SLUG, type CityKey } from "@/lib/citiesData";
import { getCityHubRoute } from "@/lib/cityHubRoutes";

const SITE_URL = "https://coloradowebimpressions.com";

// Lightweight satellite hubs not in CityKey (El Paso County radius ring).
const EXTRA_CITY_HUBS: Record<string, string> = {
  "Woodland Park": "/woodland-park",
  "Manitou Springs": "/manitou-springs",
  "Fountain": "/fountain",
  "Security-Widefield": "/security-widefield",
  "Falcon": "/falcon",
  "Black Forest": "/black-forest",
  "Palmer Lake": "/palmer-lake",
};
const CANONICAL = `${SITE_URL}/service-areas`;
const PHONE = "+1-719-419-3935";
const HQ_ADDRESS = {
  "@type": "PostalAddress" as const,
  streetAddress: "3210 E Woodmen Rd STE 110",
  addressLocality: "Colorado Springs",
  addressRegion: "CO",
  postalCode: "80920",
  addressCountry: "US",
};
const HQ_GEO = { "@type": "GeoCoordinates" as const, latitude: 38.9426, longitude: -104.7625 };

const HUB_SERVICES: { name: string; description: string; url: string }[] = [
  {
    name: "Local SEO",
    description:
      "Local SEO across Colorado — Map Pack rankings, Google Business Profile optimization, and city-specific service pages that bring qualified leads.",
    url: `${SITE_URL}/local-seo`,
  },
  {
    name: "Web Design",
    description:
      "Custom, mobile-first websites for Colorado businesses, built to convert visitors into phone calls and booked jobs.",
    url: `${SITE_URL}/web-design`,
  },
  {
    name: "Google Maps Optimization",
    description:
      "Google Business Profile setup, posts, photos, reviews and ongoing optimization to win the Google 3-Pack across Colorado.",
    url: `${SITE_URL}/local-seo`,
  },
  {
    name: "Pay-Per-Click Advertising",
    description:
      "Google Ads and Local Service Ads management for Colorado service businesses, with call tracking and transparent reporting.",
    url: `${SITE_URL}/google-ads-ppc`,
  },
  {
    name: "GEO & AI Search Optimization",
    description:
      "Get cited in ChatGPT, Perplexity, Google AI Overviews and other generative search answers for Colorado-related queries.",
    url: `${SITE_URL}/geo-ai`,
  },
];

const HUB_FAQS: { q: string; a: string }[] = [
  {
    q: "What areas of Colorado do you serve?",
    a: "We serve businesses across the entire state of Colorado — the Front Range (Denver, Colorado Springs, Aurora, Castle Rock, Monument), Northern Colorado (Fort Collins, Boulder, Loveland, Longmont), Southern Colorado (Pueblo, Cañon City, Trinidad), and the Western Slope and mountain towns (Grand Junction, Durango, Glenwood Springs, Vail, Aspen, Steamboat Springs).",
  },
  {
    q: "Do I need to be in Colorado Springs to work with you?",
    a: "No. Our office is in Colorado Springs at 3210 E Woodmen Rd, but we work with businesses across all of Colorado. Most projects run remotely with periodic in-person or video meetings — and we travel for kickoffs and quarterly reviews when it makes sense.",
  },
  {
    q: "Do you build city-specific landing pages for each Colorado market we serve?",
    a: "Yes. If your business serves multiple Colorado cities, we build unique, city-specific service pages — not duplicate content — so you can rank in each individual market for the searches your customers actually use.",
  },
  {
    q: "My city isn't listed — can you still help?",
    a: "Yes. The cities listed are highlights, not limits. If your business is anywhere in Colorado we can build a local SEO, web design or Google Maps program for your market. Reach out and we'll show you exactly how we'd rank you.",
  },
];

function buildLocalBusinessJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    // Index-page-scoped node, linked to the canonical Organization.
    "@id": `${CANONICAL}#localbusiness`,
    parentOrganization: { "@id": `${SITE_URL}/#org` },
    name: "Colorado Web Impressions",
    description:
      "Colorado web design, SEO, Google Maps, PPC and AI search marketing for local businesses across the Front Range, Northern Colorado, Southern Colorado and the Western Slope.",
    url: CANONICAL,
    telephone: PHONE,
    priceRange: "$$",
    address: HQ_ADDRESS,
    geo: HQ_GEO,
    areaServed: { "@type": "State", name: "Colorado" },
  };
}

function buildServicesJsonLd() {
  return HUB_SERVICES.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${s.name} in Colorado`,
    serviceType: s.name,
    description: s.description,
    provider: {
      "@type": "LocalBusiness",
      "@id": `${CANONICAL}#localbusiness`,
      name: "Colorado Web Impressions",
    },
    areaServed: { "@type": "State", name: "Colorado" },
    url: s.url,
  }));
}

function buildFaqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: HUB_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function buildBreadcrumbJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Service Areas", item: CANONICAL },
    ],
  };
}

export const Route = createFileRoute("/service-areas")({
  head: () => ({
    meta: [
        { name: "description", content: "Statewide Colorado service from our Colorado Springs HQ. Web design, SEO, and marketing in every Front Range city and Western Slope community." },
      {
        title: "Colorado Service Areas | Colorado Web Impressions",
      },
      {
        name: "description",
        content:
          "We serve businesses across all of Colorado — from Denver, Colorado Springs and Aurora to Grand Junction, Durango and the mountain towns. See every city we help rank and grow.",
      },
      {
        property: "og:title",
        content: "Service Areas Across Colorado | Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "Web design, SEO, PPC and Google Maps services for businesses in 60+ Colorado cities — Front Range, Northern Colorado, Southern Colorado, and the Western Slope.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Service Areas Across Colorado | Colorado Web Impressions" },
      { name: "twitter:description", content: "We serve businesses across all of Colorado — from Denver, Colorado Springs and Aurora to Grand Junction, Durango and the mountain towns. See every city we help rank and grow." },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(buildLocalBusinessJsonLd()) },
      ...buildServicesJsonLd().map((s) => ({
        type: "application/ld+json" as const,
        children: JSON.stringify(s),
      })),
      { type: "application/ld+json", children: JSON.stringify(buildFaqJsonLd()) },
      { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbJsonLd()) },
    ],
  }),
  component: ServiceAreasPage,
});

const regions = [
  {
    name: "Front Range",
    blurb: "The I-25 corridor — where most Colorado businesses live and compete.",
    cities: [
      "Denver",
      "Colorado Springs",
      "Aurora",
      "Lakewood",
      "Thornton",
      "Arvada",
      "Westminster",
      "Centennial",
      "Highlands Ranch",
      "Castle Rock",
      "Parker",
      "Littleton",
      "Englewood",
      "Wheat Ridge",
      "Commerce City",
      "Brighton",
      "Northglenn",
      "Monument",
      "Palmer Lake",
      "Fountain",
      "Security-Widefield",
      "Falcon",
      "Black Forest",
    ],
  },
  {
    name: "Northern Colorado",
    blurb: "Fort Collins, Boulder County, and the fast-growing northern I-25 cities.",
    cities: [
      "Fort Collins",
      "Boulder",
      "Greeley",
      "Loveland",
      "Longmont",
      "Broomfield",
      "Erie",
      "Lafayette",
      "Louisville",
      "Windsor",
      "Wellington",
      "Berthoud",
      "Estes Park",
      "Evans",
      "Johnstown",
      "Frederick",
      "Firestone",
    ],
  },
  {
    name: "Southern Colorado",
    blurb: "Pueblo and the southern communities along I-25 and the Arkansas River.",
    cities: [
      "Pueblo",
      "Pueblo West",
      "Cañon City",
      "Trinidad",
      "Salida",
      "Woodland Park",
      "Manitou Springs",
      "La Junta",
      "Lamar",
      "Alamosa",
      "Florence",
      "Walsenburg",
    ],
  },
  {
    name: "Western Slope & Mountains",
    blurb: "Resort towns, mountain communities, and the cities west of the Continental Divide.",
    cities: [
      "Grand Junction",
      "Durango",
      "Glenwood Springs",
      "Steamboat Springs",
      "Vail",
      "Aspen",
      "Breckenridge",
      "Frisco",
      "Silverthorne",
      "Dillon",
      "Avon",
      "Edwards",
      "Eagle",
      "Gunnison",
      "Crested Butte",
      "Telluride",
      "Montrose",
      "Cortez",
      "Pagosa Springs",
      "Rifle",
      "Carbondale",
      "Fruita",
    ],
  },
];

function ServiceAreasPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado Marketing Service Areas"
        title={
          <>
            We serve businesses in every corner of{" "}
            <span className="text-gradient-gold">Colorado</span>
          </>
        }
        description="From the Front Range metros to the Western Slope mountain towns, we help local businesses rank on Google, win the Map Pack, and turn their websites into lead-generating machines."
      />

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 space-y-16">
          {regions.map((r) => (
            <div key={r.name}>
              <div className="flex flex-col items-start gap-2 border-b border-border pb-5">
                <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                  <MapPin className="h-3.5 w-3.5" />
                  {r.name}
                </div>
                <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                  {r.cities.length} cities served across {r.name}
                </h2>
                <p className="text-muted-foreground">{r.blurb}</p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                {r.cities.map((c) => {
                  const slug: CityKey | undefined = CITY_NAME_TO_SLUG[c];
                  const extraHub = EXTRA_CITY_HUBS[c];
                  const hubHref = slug ? getCityHubRoute(slug) : extraHub;
                  const cls =
                    "inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-card transition-smooth hover:border-brand-gold/50 hover:bg-brand-gold/5";
                  const inner = (
                    <>
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-brand-gold-deep" />
                      <span className="flex-1 text-left">{c}</span>
                      {hubHref && (
                        <span className="rounded-full bg-brand-gold/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-gold-deep">
                          Hub
                        </span>
                      )}
                      {slug && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-brand-gold-deep">
                          →
                        </span>
                      )}
                    </>
                  );
                  // Prefer the dedicated `-digital-marketing` hub when one
                  // exists — it's the conversion-focused, ranking-target
                  // page for that city. Fall back to the generic
                  // /service-areas/{slug} overview otherwise.
                  if (hubHref) {
                    return (
                      <a key={c} href={hubHref} className={cls}>
                        {inner}
                      </a>
                    );
                  }
                  return slug ? (
                    <Link
                      key={c}
                      to="/service-areas/$citySlug"
                      params={{ citySlug: slug }}
                      className={cls}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div key={c} className={cls}>
                      {inner}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-brand-black via-zinc-900 to-brand-black p-10 text-center shadow-card sm:p-14">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-gold/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-gold/10 blur-3xl" />
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-gold" />

            <div className="relative">
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                Don&apos;t see your <span className="text-gradient-gold">Colorado city?</span>
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-white/90">
                If your business is anywhere in Colorado, we can help. Reach out and we&apos;ll
                tell you exactly how we&apos;d rank you in your market.
              </p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-xl bg-gradient-gold px-6 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                >
                  Get a free strategy call
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/industries"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/5 px-6 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
                >
                  See industries we serve
                </Link>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-4xl">
            <div className="text-center">
              <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                Service Area FAQs
              </span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Common questions about our Colorado coverage
              </h2>
            </div>
            <div className="mt-8 space-y-4">
              {HUB_FAQS.map((f) => (
                <div
                  key={f.q}
                  className="rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <h3 className="text-base font-bold text-foreground">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
